import { MessageFormData } from 'mk2/apps/bazaar/components/MessageForm';
import { ReviewFormData } from 'mk2/apps/bazaar/components/ReviewForm';
import { DeletedReason } from 'mk2/apps/bazaar/schemas';
import { complaintOpenTrigger } from 'mk2/containers/Complaint/Complaint.actions';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import { APIFailureAction, APIRequestAction, APISuccessAction } from 'mk2/helpers/action';
import { NormalizedResponse } from 'mk2/helpers/api';
import { Action } from 'redux';

export interface ProductDetailLoadApiAction extends Action {
    readonly slug: string;
}

export const PRODUCT_DETAIL_LOAD_API_REQUEST = 'PRODUCT_DETAIL_LOAD_API_REQUEST';

export interface ProductDetailLoadApiRequestAction extends ProductDetailLoadApiAction, APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_LOAD_API_REQUEST;
}

export interface ProductDetailLoadApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        product: number;
        marks: number[];
        complaints: number[];
        messages: number[];
        reviews: number[];
        buyers: number[];
        closedBuyers: number[];
        sellers: number[];
        relatedProducts: number[];
        lastNote: number;
        stroller?: number;
        strollerReviewedBySeller?: boolean;
    };
}

export const PRODUCT_DETAIL_LOAD_API_SUCCESS = 'PRODUCT_DETAIL_LOAD_API_SUCCESS';

export interface ProductDetailLoadApiSuccessAction
    extends ProductDetailLoadApiAction,
        APISuccessAction<ProductDetailLoadApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_LOAD_API_SUCCESS;
    readonly viewsCount: number;
    readonly historyCount: number;
    readonly sellerPhone: string;
}

export const PRODUCT_DETAIL_LOAD_API_FAILURE = 'PRODUCT_DETAIL_LOAD_API_FAILURE';

export interface ProductDetailLoadErrorApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        relatedProducts: number[];
    };
}

export interface ProductDetailLoadApiFailureAction extends ProductDetailLoadApiAction, APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_LOAD_API_FAILURE;
    readonly response: ProductDetailLoadErrorApiNormalizedResponse;
}

export const productDetailLoadApi = {
    request: (slug: string): ProductDetailLoadApiRequestAction => ({ type: PRODUCT_DETAIL_LOAD_API_REQUEST, slug }),
    success: (
        slug: string,
        viewsCount: number,
        historyCount: number,
        sellerPhone: string,
        response: ProductDetailLoadApiNormalizedResponse,
    ): ProductDetailLoadApiSuccessAction => ({
        type: PRODUCT_DETAIL_LOAD_API_SUCCESS,
        slug,
        viewsCount,
        historyCount,
        sellerPhone,
        response,
    }),
    failure: (
        slug: string,
        error: any,
        response: ProductDetailLoadErrorApiNormalizedResponse,
    ): ProductDetailLoadApiFailureAction => ({
        type: PRODUCT_DETAIL_LOAD_API_FAILURE,
        slug,
        error,
        response,
    }),
};

export const PRODUCT_DETAIL_LOAD = 'PRODUCT_DETAIL_LOAD';

export interface ProductDetailLoadAction extends ProductDetailLoadApiAction {
    readonly type: typeof PRODUCT_DETAIL_LOAD;
}

export const productDetailLoad = (slug: string): ProductDetailLoadAction => ({ type: PRODUCT_DETAIL_LOAD, slug });

export interface ProductDetailActivateProductApiAction extends Action {
    readonly productId: number;
}

export const PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_REQUEST = 'PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_REQUEST';

export interface ProductDetailActivateProductApiRequestAction
    extends ProductDetailActivateProductApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_REQUEST;
}

export interface ProductDetailActivateProductApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        product: number;
    };
}

export const PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_SUCCESS = 'PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_SUCCESS';

export interface ProductDetailActivateProductApiSuccessAction
    extends ProductDetailActivateProductApiAction,
        APISuccessAction<ProductDetailActivateProductApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_SUCCESS;
}

export const PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_FAILURE = 'PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_FAILURE';

export interface ProductDetailActivateProductApiFailureAction
    extends ProductDetailActivateProductApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_FAILURE;
}

export const productDetailActivateProductApi = {
    request: (productId: number): ProductDetailActivateProductApiRequestAction => ({
        type: PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_REQUEST,
        productId,
    }),
    success: (
        productId: number,
        response: ProductDetailActivateProductApiNormalizedResponse,
    ): ProductDetailActivateProductApiSuccessAction => ({
        type: PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_SUCCESS,
        productId,
        response,
    }),
    failure: (productId: number, error: any): ProductDetailActivateProductApiFailureAction => ({
        type: PRODUCT_DETAIL_ACTIVATE_PRODUCT_API_FAILURE,
        productId,
        error,
    }),
};

export const PRODUCT_DETAIL_ACTIVATE_PRODUCT = 'PRODUCT_DETAIL_ACTIVATE_PRODUCT';

export interface ProductDetailActivateProductAction extends ProductDetailActivateProductApiAction {
    readonly type: typeof PRODUCT_DETAIL_ACTIVATE_PRODUCT;
}

export const productDetailActivateProduct = (productId: number): ProductDetailActivateProductAction => ({
    type: PRODUCT_DETAIL_ACTIVATE_PRODUCT,
    productId,
});

export interface ProductDetailDeactivateProductApiAction extends Action {
    readonly productId: number;
}

export const PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_REQUEST = 'PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_REQUEST';

export interface ProductDetailDeactivateProductApiRequestAction
    extends ProductDetailDeactivateProductApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_REQUEST;
}

export interface ProductDetailDeactivateProductApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        product: number;
    };
}

export const PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_SUCCESS = 'PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_SUCCESS';

export interface ProductDetailDeactivateProductApiSuccessAction
    extends ProductDetailDeactivateProductApiAction,
        APISuccessAction<ProductDetailDeactivateProductApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_SUCCESS;
}

export const PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_FAILURE = 'PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_FAILURE';

export interface ProductDetailDeactivateProductApiFailureAction
    extends ProductDetailDeactivateProductApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_FAILURE;
}

export const productDetailDeactivateProductApi = {
    request: (productId: number): ProductDetailDeactivateProductApiRequestAction => ({
        type: PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_REQUEST,
        productId,
    }),
    success: (
        productId: number,
        response: ProductDetailDeactivateProductApiNormalizedResponse,
    ): ProductDetailDeactivateProductApiSuccessAction => ({
        type: PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_SUCCESS,
        productId,
        response,
    }),
    failure: (productId: number, error: any): ProductDetailDeactivateProductApiFailureAction => ({
        type: PRODUCT_DETAIL_DEACTIVATE_PRODUCT_API_FAILURE,
        productId,
        error,
    }),
};

export const PRODUCT_DETAIL_DEACTIVATE_PRODUCT = 'PRODUCT_DETAIL_DEACTIVATE_PRODUCT';

export interface ProductDetailDeactivateProductAction extends ProductDetailDeactivateProductApiAction {
    readonly type: typeof PRODUCT_DETAIL_DEACTIVATE_PRODUCT;
}

export const productDetailDeactivateProduct = (productId: number): ProductDetailDeactivateProductAction => ({
    type: PRODUCT_DETAIL_DEACTIVATE_PRODUCT,
    productId,
});

export interface ProductDetailDeleteProductApiAction extends Action {
    readonly productId: number;
    readonly reason: DeletedReason;
}

export const PRODUCT_DETAIL_DELETE_PRODUCT_API_REQUEST = 'PRODUCT_DETAIL_DELETE_PRODUCT_API_REQUEST';

export interface ProductDetailDeleteProductApiRequestAction
    extends ProductDetailDeleteProductApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_DELETE_PRODUCT_API_REQUEST;
}

export interface ProductDetailDeleteProductApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        product: number;
    };
}

export const PRODUCT_DETAIL_DELETE_PRODUCT_API_SUCCESS = 'PRODUCT_DETAIL_DELETE_PRODUCT_API_SUCCESS';

export interface ProductDetailDeleteProductApiSuccessAction
    extends ProductDetailDeleteProductApiAction,
        APISuccessAction<ProductDetailDeleteProductApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_DELETE_PRODUCT_API_SUCCESS;
}

export const PRODUCT_DETAIL_DELETE_PRODUCT_API_FAILURE = 'PRODUCT_DETAIL_DELETE_PRODUCT_API_FAILURE';

export interface ProductDetailDeleteProductApiFailureAction
    extends ProductDetailDeleteProductApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_DELETE_PRODUCT_API_FAILURE;
}

export const productDetailDeleteProductApi = {
    request: (productId: number, reason: DeletedReason): ProductDetailDeleteProductApiRequestAction => ({
        type: PRODUCT_DETAIL_DELETE_PRODUCT_API_REQUEST,
        productId,
        reason,
    }),
    success: (
        productId: number,
        reason: DeletedReason,
        response: ProductDetailDeleteProductApiNormalizedResponse,
    ): ProductDetailDeleteProductApiSuccessAction => ({
        type: PRODUCT_DETAIL_DELETE_PRODUCT_API_SUCCESS,
        productId,
        reason,
        response,
    }),
    failure: (productId: number, reason: DeletedReason, error: any): ProductDetailDeleteProductApiFailureAction => ({
        type: PRODUCT_DETAIL_DELETE_PRODUCT_API_FAILURE,
        productId,
        reason,
        error,
    }),
};

export const PRODUCT_DETAIL_DELETE_PRODUCT = 'PRODUCT_DETAIL_DELETE_PRODUCT';

export interface ProductDetailDeleteProductAction extends ProductDetailDeleteProductApiAction {
    readonly type: typeof PRODUCT_DETAIL_DELETE_PRODUCT;
}

export const productDetailDeleteProduct = (
    productId: number,
    reason: DeletedReason,
): ProductDetailDeleteProductAction => ({
    type: PRODUCT_DETAIL_DELETE_PRODUCT,
    productId,
    reason,
});

export interface ProductDetailRepostProductApiAction extends Action {
    readonly productId: number;
}

export const PRODUCT_DETAIL_REPOST_PRODUCT_API_REQUEST = 'PRODUCT_DETAIL_REPOST_PRODUCT_API_REQUEST';

export interface ProductDetailRepostProductApiRequestAction
    extends ProductDetailRepostProductApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_REPOST_PRODUCT_API_REQUEST;
}

export interface ProductDetailRepostProductApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        product: number;
    };
}

export const PRODUCT_DETAIL_REPOST_PRODUCT_API_SUCCESS = 'PRODUCT_DETAIL_REPOST_PRODUCT_API_SUCCESS';

export interface ProductDetailRepostProductApiSuccessAction
    extends ProductDetailRepostProductApiAction,
        APISuccessAction<ProductDetailRepostProductApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_REPOST_PRODUCT_API_SUCCESS;
}

export const PRODUCT_DETAIL_REPOST_PRODUCT_API_FAILURE = 'PRODUCT_DETAIL_REPOST_PRODUCT_API_FAILURE';

export interface ProductDetailRepostProductApiFailureAction
    extends ProductDetailRepostProductApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_REPOST_PRODUCT_API_FAILURE;
}

export const productDetailRepostProductApi = {
    request: (productId: number): ProductDetailRepostProductApiRequestAction => ({
        type: PRODUCT_DETAIL_REPOST_PRODUCT_API_REQUEST,
        productId,
    }),
    success: (
        productId: number,
        response: ProductDetailRepostProductApiNormalizedResponse,
    ): ProductDetailRepostProductApiSuccessAction => ({
        type: PRODUCT_DETAIL_REPOST_PRODUCT_API_SUCCESS,
        productId,
        response,
    }),
    failure: (productId: number, error: any): ProductDetailRepostProductApiFailureAction => ({
        type: PRODUCT_DETAIL_REPOST_PRODUCT_API_FAILURE,
        productId,
        error,
    }),
};

export const PRODUCT_DETAIL_REPOST_PRODUCT = 'PRODUCT_DETAIL_REPOST_PRODUCT';

export interface ProductDetailRepostProductAction extends ProductDetailRepostProductApiAction {
    readonly type: typeof PRODUCT_DETAIL_REPOST_PRODUCT;
}

export const productDetailRepostProduct = (productId: number): ProductDetailRepostProductAction => ({
    type: PRODUCT_DETAIL_REPOST_PRODUCT,
    productId,
});

export const productDetailReportProduct = (productId: number) =>
    complaintOpenTrigger(ComplaintTargetType.BazaarProduct, productId);

export interface ProductDetailMarkOKProductApiAction extends Action {
    readonly productId: number;
}

export const PRODUCT_DETAIL_MARK_OK_PRODUCT_API_REQUEST = 'PRODUCT_DETAIL_MARK_OK_PRODUCT_API_REQUEST';

export interface ProductDetailMarkOKProductApiRequestAction
    extends ProductDetailMarkOKProductApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_MARK_OK_PRODUCT_API_REQUEST;
}

export interface ProductDetailMarkOKProductApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        product: number;
        marks: number[];
    };
}

export const PRODUCT_DETAIL_MARK_OK_PRODUCT_API_SUCCESS = 'PRODUCT_DETAIL_MARK_OK_PRODUCT_API_SUCCESS';

export interface ProductDetailMarkOKProductApiSuccessAction
    extends ProductDetailMarkOKProductApiAction,
        APISuccessAction<ProductDetailMarkOKProductApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_MARK_OK_PRODUCT_API_SUCCESS;
}

export const PRODUCT_DETAIL_MARK_OK_PRODUCT_API_FAILURE = 'PRODUCT_DETAIL_MARK_OK_PRODUCT_API_FAILURE';

export interface ProductDetailMarkOKProductApiFailureAction
    extends ProductDetailMarkOKProductApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_MARK_OK_PRODUCT_API_FAILURE;
}

export const productDetailMarkOKProductApi = {
    request: (productId: number): ProductDetailMarkOKProductApiRequestAction => ({
        type: PRODUCT_DETAIL_MARK_OK_PRODUCT_API_REQUEST,
        productId,
    }),
    success: (
        productId: number,
        response: ProductDetailMarkOKProductApiNormalizedResponse,
    ): ProductDetailMarkOKProductApiSuccessAction => ({
        type: PRODUCT_DETAIL_MARK_OK_PRODUCT_API_SUCCESS,
        productId,
        response,
    }),
    failure: (productId: number, error: any): ProductDetailMarkOKProductApiFailureAction => ({
        type: PRODUCT_DETAIL_MARK_OK_PRODUCT_API_FAILURE,
        productId,
        error,
    }),
};

export const PRODUCT_DETAIL_MARK_OK_PRODUCT = 'PRODUCT_DETAIL_MARK_OK_PRODUCT';

export interface ProductDetailMarkOKProductAction extends ProductDetailMarkOKProductApiAction {
    readonly type: typeof PRODUCT_DETAIL_MARK_OK_PRODUCT;
}

export const productDetailMarkOKProduct = (productId: number): ProductDetailMarkOKProductAction => ({
    type: PRODUCT_DETAIL_MARK_OK_PRODUCT,
    productId,
});

export interface ProductDetailNewMessageApiAction extends Action {
    readonly productId: number;
    readonly slug: string;
    readonly data: MessageFormData;
}

export const PRODUCT_DETAIL_NEW_MESSAGE_API_REQUEST = 'PRODUCT_DETAIL_NEW_MESSAGE_API_REQUEST';

export interface ProductDetailNewMessageApiRequestAction extends ProductDetailNewMessageApiAction, APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_MESSAGE_API_REQUEST;
}

export interface ProductDetailNewMessageApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        message: number;
        buyer: number;
        seller: number;
    };
}

export const PRODUCT_DETAIL_NEW_MESSAGE_API_SUCCESS = 'PRODUCT_DETAIL_NEW_MESSAGE_API_SUCCESS';

export interface ProductDetailNewMessageApiSuccessAction
    extends ProductDetailNewMessageApiAction,
        APISuccessAction<ProductDetailNewMessageApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_NEW_MESSAGE_API_SUCCESS;
}

export const PRODUCT_DETAIL_NEW_MESSAGE_API_FAILURE = 'PRODUCT_DETAIL_NEW_MESSAGE_API_FAILURE';

export interface ProductDetailNewMessageApiFailureAction extends ProductDetailNewMessageApiAction, APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_MESSAGE_API_FAILURE;
}

export const productDetailNewMessageApi = {
    request: (productId: number, slug: string, data: MessageFormData): ProductDetailNewMessageApiRequestAction => ({
        type: PRODUCT_DETAIL_NEW_MESSAGE_API_REQUEST,
        productId,
        slug,
        data,
    }),
    success: (
        productId: number,
        slug: string,
        data: MessageFormData,
        response: ProductDetailNewMessageApiNormalizedResponse,
    ): ProductDetailNewMessageApiSuccessAction => ({
        type: PRODUCT_DETAIL_NEW_MESSAGE_API_SUCCESS,
        productId,
        slug,
        data,
        response,
    }),
    failure: (
        productId: number,
        slug: string,
        data: MessageFormData,
        error: any,
    ): ProductDetailNewMessageApiFailureAction => ({
        type: PRODUCT_DETAIL_NEW_MESSAGE_API_FAILURE,
        productId,
        slug,
        data,
        error,
    }),
};

export const PRODUCT_DETAIL_NEW_MESSAGE = 'PRODUCT_DETAIL_NEW_MESSAGE';

export interface ProductDetailNewMessageAction extends ProductDetailNewMessageApiAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_MESSAGE;
}

export const productDetailNewMessage = (
    productId: number,
    slug: string,
    data: MessageFormData,
): ProductDetailNewMessageAction => ({
    type: PRODUCT_DETAIL_NEW_MESSAGE,
    productId,
    slug,
    data,
});

export interface ProductDetailReadMessagesApiAction extends Action {
    readonly messagesIds: number[];
}

export const PRODUCT_DETAIL_READ_MESSAGES_API_REQUEST = 'PRODUCT_DETAIL_READ_MESSAGES_API_REQUEST';

export interface ProductDetailReadMessagesApiRequestAction
    extends ProductDetailReadMessagesApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_READ_MESSAGES_API_REQUEST;
}

export interface ProductDetailReadMessagesApiNormalizedResponse extends NormalizedResponse {
    readonly result: {};
}

export const PRODUCT_DETAIL_READ_MESSAGES_API_SUCCESS = 'PRODUCT_DETAIL_READ_MESSAGES_API_SUCCESS';

export interface ProductDetailReadMessagesApiSuccessAction
    extends ProductDetailReadMessagesApiAction,
        APISuccessAction<ProductDetailReadMessagesApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_READ_MESSAGES_API_SUCCESS;
}

export const PRODUCT_DETAIL_READ_MESSAGES_API_FAILURE = 'PRODUCT_DETAIL_READ_MESSAGES_API_FAILURE';

export interface ProductDetailReadMessagesApiFailureAction
    extends ProductDetailReadMessagesApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_READ_MESSAGES_API_FAILURE;
}

export const productDetailReadMessagesApi = {
    request: (messagesIds: number[]): ProductDetailReadMessagesApiRequestAction => ({
        type: PRODUCT_DETAIL_READ_MESSAGES_API_REQUEST,
        messagesIds,
    }),
    success: (
        messagesIds: number[],
        response: ProductDetailReadMessagesApiNormalizedResponse,
    ): ProductDetailReadMessagesApiSuccessAction => ({
        type: PRODUCT_DETAIL_READ_MESSAGES_API_SUCCESS,
        messagesIds,
        response,
    }),
    failure: (messagesIds: number[], error: any): ProductDetailReadMessagesApiFailureAction => ({
        type: PRODUCT_DETAIL_READ_MESSAGES_API_FAILURE,
        messagesIds,
        error,
    }),
};

export const PRODUCT_DETAIL_READ_MESSAGES = 'PRODUCT_DETAIL_READ_MESSAGES';

export interface ProductDetailReadMessagesAction extends ProductDetailReadMessagesApiAction {
    readonly type: typeof PRODUCT_DETAIL_READ_MESSAGES;
}

export const productDetailReadMessages = (messagesIds: number[]): ProductDetailReadMessagesAction => ({
    type: PRODUCT_DETAIL_READ_MESSAGES,
    messagesIds,
});

export interface ProductDetailNewReviewApiAction extends Action {
    readonly buyerId: number;
    readonly slug: string; // Product slug for reducer
    readonly data: ReviewFormData;
}

export const PRODUCT_DETAIL_NEW_REVIEW_API_REQUEST = 'PRODUCT_DETAIL_NEW_REVIEW_API_REQUEST';

export interface ProductDetailNewReviewApiRequestAction extends ProductDetailNewReviewApiAction, APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_REVIEW_API_REQUEST;
}

export interface ProductDetailNewReviewApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        review: number;
    };
}

export const PRODUCT_DETAIL_NEW_REVIEW_API_SUCCESS = 'PRODUCT_DETAIL_NEW_REVIEW_API_SUCCESS';

export interface ProductDetailNewReviewApiSuccessAction
    extends ProductDetailNewReviewApiAction,
        APISuccessAction<ProductDetailNewReviewApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_NEW_REVIEW_API_SUCCESS;
}

export const PRODUCT_DETAIL_NEW_REVIEW_API_FAILURE = 'PRODUCT_DETAIL_NEW_REVIEW_API_FAILURE';

export interface ProductDetailNewReviewApiFailureAction extends ProductDetailNewReviewApiAction, APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_REVIEW_API_FAILURE;
}

export const productDetailNewReviewApi = {
    request: (buyerId: number, slug: string, data: ReviewFormData): ProductDetailNewReviewApiRequestAction => ({
        type: PRODUCT_DETAIL_NEW_REVIEW_API_REQUEST,
        buyerId,
        slug,
        data,
    }),
    success: (
        buyerId: number,
        slug: string,
        data: ReviewFormData,
        response: ProductDetailNewReviewApiNormalizedResponse,
    ): ProductDetailNewReviewApiSuccessAction => ({
        type: PRODUCT_DETAIL_NEW_REVIEW_API_SUCCESS,
        buyerId,
        slug,
        data,
        response,
    }),
    failure: (
        buyerId: number,
        slug: string,
        data: ReviewFormData,
        error: any,
    ): ProductDetailNewReviewApiFailureAction => ({
        type: PRODUCT_DETAIL_NEW_REVIEW_API_FAILURE,
        buyerId,
        slug,
        data,
        error,
    }),
};

export const PRODUCT_DETAIL_NEW_REVIEW = 'PRODUCT_DETAIL_NEW_REVIEW';

export interface ProductDetailNewReviewAction extends ProductDetailNewReviewApiAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_REVIEW;
}

export const productDetailNewReview = (
    buyerId: number,
    slug: string,
    data: ReviewFormData,
): ProductDetailNewReviewAction => ({ type: PRODUCT_DETAIL_NEW_REVIEW, buyerId, slug, data });

export interface ProductDetailNewReplyApiAction extends Action {
    readonly buyerId: number;
    readonly slug: string;
    readonly data: MessageFormData;
}

export const PRODUCT_DETAIL_NEW_REPLY_API_REQUEST = 'PRODUCT_DETAIL_NEW_REPLY_API_REQUEST';

export interface ProductDetailNewReplyApiRequestAction extends ProductDetailNewReplyApiAction, APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_REPLY_API_REQUEST;
}

export interface ProductDetailNewReplyApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        message: number;
        buyer: number;
    };
}

export const PRODUCT_DETAIL_NEW_REPLY_API_SUCCESS = 'PRODUCT_DETAIL_NEW_REPLY_API_SUCCESS';

export interface ProductDetailNewReplyApiSuccessAction
    extends ProductDetailNewReplyApiAction,
        APISuccessAction<ProductDetailNewReplyApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_NEW_REPLY_API_SUCCESS;
}

export const PRODUCT_DETAIL_NEW_REPLY_API_FAILURE = 'PRODUCT_DETAIL_NEW_REPLY_API_FAILURE';

export interface ProductDetailNewReplyApiFailureAction extends ProductDetailNewReplyApiAction, APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_REPLY_API_FAILURE;
}

export const productDetailNewReplyApi = {
    request: (buyerId: number, slug: string, data: MessageFormData): ProductDetailNewReplyApiRequestAction => ({
        type: PRODUCT_DETAIL_NEW_REPLY_API_REQUEST,
        buyerId,
        slug,
        data,
    }),
    success: (
        buyerId: number,
        slug: string,
        data: MessageFormData,
        response: ProductDetailNewReplyApiNormalizedResponse,
    ): ProductDetailNewReplyApiSuccessAction => ({
        type: PRODUCT_DETAIL_NEW_REPLY_API_SUCCESS,
        buyerId,
        slug,
        data,
        response,
    }),
    failure: (
        buyerId: number,
        slug: string,
        data: MessageFormData,
        error: any,
    ): ProductDetailNewReplyApiFailureAction => ({
        type: PRODUCT_DETAIL_NEW_REPLY_API_FAILURE,
        buyerId,
        slug,
        data,
        error,
    }),
};

export const PRODUCT_DETAIL_NEW_REPLY = 'PRODUCT_DETAIL_NEW_REPLY';

export interface ProductDetailNewReplyAction extends ProductDetailNewReplyApiAction {
    readonly type: typeof PRODUCT_DETAIL_NEW_REPLY;
}

export const productDetailNewReply = (
    buyerId: number,
    slug: string,
    data: MessageFormData,
): ProductDetailNewReplyAction => ({
    type: PRODUCT_DETAIL_NEW_REPLY,
    buyerId,
    slug,
    data,
});

export interface ProductDetailRemoveBuyerApiAction extends Action {
    readonly slug: string;
    readonly buyerId: number;
}

export const PRODUCT_DETAIL_REMOVE_BUYER_API_REQUEST = 'PRODUCT_DETAIL_REMOVE_BUYER_API_REQUEST';

export interface ProductDetailRemoveBuyerApiRequestAction extends ProductDetailRemoveBuyerApiAction, APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_REMOVE_BUYER_API_REQUEST;
}

export interface ProductDetailRemoveBuyerApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        buyer: number;
    };
}

export const PRODUCT_DETAIL_REMOVE_BUYER_API_SUCCESS = 'PRODUCT_DETAIL_REMOVE_BUYER_API_SUCCESS';

export interface ProductDetailRemoveBuyerApiSuccessAction
    extends ProductDetailRemoveBuyerApiAction,
        APISuccessAction<ProductDetailRemoveBuyerApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_REMOVE_BUYER_API_SUCCESS;
}

export const PRODUCT_DETAIL_REMOVE_BUYER_API_FAILURE = 'PRODUCT_DETAIL_REMOVE_BUYER_API_FAILURE';

export interface ProductDetailRemoveBuyerApiFailureAction extends ProductDetailRemoveBuyerApiAction, APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_REMOVE_BUYER_API_FAILURE;
}

export const productDetailRemoveBuyerApi = {
    request: (slug: string, buyerId: number): ProductDetailRemoveBuyerApiRequestAction => ({
        type: PRODUCT_DETAIL_REMOVE_BUYER_API_REQUEST,
        slug,
        buyerId,
    }),
    success: (
        slug: string,
        buyerId: number,
        response: ProductDetailRemoveBuyerApiNormalizedResponse,
    ): ProductDetailRemoveBuyerApiSuccessAction => ({
        type: PRODUCT_DETAIL_REMOVE_BUYER_API_SUCCESS,
        slug,
        buyerId,
        response,
    }),
    failure: (slug: string, buyerId: number, error: any): ProductDetailRemoveBuyerApiFailureAction => ({
        type: PRODUCT_DETAIL_REMOVE_BUYER_API_FAILURE,
        slug,
        buyerId,
        error,
    }),
};

export const PRODUCT_DETAIL_REMOVE_BUYER = 'PRODUCT_DETAIL_REMOVE_BUYER';

export interface ProductDetailRemoveBuyerAction extends ProductDetailRemoveBuyerApiAction {
    readonly type: typeof PRODUCT_DETAIL_REMOVE_BUYER;
}

export const productDetailRemoveBuyer = (slug: string, buyerId: number): ProductDetailRemoveBuyerAction => ({
    type: PRODUCT_DETAIL_REMOVE_BUYER,
    slug,
    buyerId,
});

export interface ProductDetailRestoreBuyerApiAction extends Action {
    readonly slug: string;
    readonly buyerId: number;
}

export const PRODUCT_DETAIL_RESTORE_BUYER_API_REQUEST = 'PRODUCT_DETAIL_RESTORE_BUYER_API_REQUEST';

export interface ProductDetailRestoreBuyerApiRequestAction
    extends ProductDetailRestoreBuyerApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_RESTORE_BUYER_API_REQUEST;
}

export interface ProductDetailRestoreBuyerApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        buyer: number;
    };
}

export const PRODUCT_DETAIL_RESTORE_BUYER_API_SUCCESS = 'PRODUCT_DETAIL_RESTORE_BUYER_API_SUCCESS';

export interface ProductDetailRestoreBuyerApiSuccessAction
    extends ProductDetailRestoreBuyerApiAction,
        APISuccessAction<ProductDetailRestoreBuyerApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_RESTORE_BUYER_API_SUCCESS;
}

export const PRODUCT_DETAIL_RESTORE_BUYER_API_FAILURE = 'PRODUCT_DETAIL_RESTORE_BUYER_API_FAILURE';

export interface ProductDetailRestoreBuyerApiFailureAction
    extends ProductDetailRestoreBuyerApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_RESTORE_BUYER_API_FAILURE;
}

export const productDetailRestoreBuyerApi = {
    request: (slug: string, buyerId: number): ProductDetailRestoreBuyerApiRequestAction => ({
        type: PRODUCT_DETAIL_RESTORE_BUYER_API_REQUEST,
        slug,
        buyerId,
    }),
    success: (
        slug: string,
        buyerId: number,
        response: ProductDetailRestoreBuyerApiNormalizedResponse,
    ): ProductDetailRestoreBuyerApiSuccessAction => ({
        type: PRODUCT_DETAIL_RESTORE_BUYER_API_SUCCESS,
        slug,
        buyerId,
        response,
    }),
    failure: (slug: string, buyerId: number, error: any): ProductDetailRestoreBuyerApiFailureAction => ({
        type: PRODUCT_DETAIL_RESTORE_BUYER_API_FAILURE,
        slug,
        buyerId,
        error,
    }),
};

export const PRODUCT_DETAIL_RESTORE_BUYER = 'PRODUCT_DETAIL_RESTORE_BUYER';

export interface ProductDetailRestoreBuyerAction extends ProductDetailRestoreBuyerApiAction {
    readonly type: typeof PRODUCT_DETAIL_RESTORE_BUYER;
}

export const productDetailRestoreBuyer = (slug: string, buyerId: number): ProductDetailRestoreBuyerAction => ({
    type: PRODUCT_DETAIL_RESTORE_BUYER,
    slug,
    buyerId,
});

export interface ProductDetailViewApiAction extends Action {
    readonly productId: number;
    readonly productSlug: string;
}

export const PRODUCT_DETAIL_VIEW_API_REQUEST = 'PRODUCT_DETAIL_VIEW_API_REQUEST';

export interface ProductDetailViewApiRequestAction extends ProductDetailViewApiAction, APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_VIEW_API_REQUEST;
}

export interface ProductDetailViewApiNormalizedResponse extends NormalizedResponse {
    readonly result: {};
}

export const PRODUCT_DETAIL_VIEW_API_SUCCESS = 'PRODUCT_DETAIL_VIEW_API_SUCCESS';

export interface ProductDetailViewApiSuccessAction
    extends ProductDetailViewApiAction,
        APISuccessAction<ProductDetailViewApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_VIEW_API_SUCCESS;
    readonly viewsCount: number;
}

export const PRODUCT_DETAIL_VIEW_API_FAILURE = 'PRODUCT_DETAIL_VIEW_API_FAILURE';

export interface ProductDetailViewApiFailureAction extends ProductDetailViewApiAction, APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_VIEW_API_FAILURE;
}

export const productDetailViewApi = {
    request: (productId: number, productSlug: string): ProductDetailViewApiRequestAction => ({
        type: PRODUCT_DETAIL_VIEW_API_REQUEST,
        productId,
        productSlug,
    }),
    success: (
        productId: number,
        productSlug: string,
        viewsCount: number,
        response: ProductDetailViewApiNormalizedResponse,
    ): ProductDetailViewApiSuccessAction => ({
        type: PRODUCT_DETAIL_VIEW_API_SUCCESS,
        productId,
        productSlug,
        viewsCount,
        response,
    }),
    failure: (productId: number, productSlug: string, error: any): ProductDetailViewApiFailureAction => ({
        type: PRODUCT_DETAIL_VIEW_API_FAILURE,
        productId,
        productSlug,
        error,
    }),
};

export const PRODUCT_DETAIL_VIEW = 'PRODUCT_DETAIL_VIEW';

export interface ProductDetailViewAction extends ProductDetailViewApiAction {
    readonly type: typeof PRODUCT_DETAIL_VIEW;
}

export const productDetailView = (productId: number, productSlug: string): ProductDetailViewAction => ({
    type: PRODUCT_DETAIL_VIEW,
    productId,
    productSlug,
});

/////////////////////////////////////////////////////////////////////
// Set/unset "show seller phone"

export interface ProductDetailSetShowSellerPhoneApiAction extends Action {
    readonly value: boolean;
    readonly slug: string;
    readonly phoneVerified: boolean;
    readonly sellerId: number;
}

export const PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_REQUEST = 'PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_REQUEST';

export interface ProductDetailSetShowSellerPhoneApiRequestAction
    extends ProductDetailSetShowSellerPhoneApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_REQUEST;
}

export interface ProductDetailSetShowSellerPhoneApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        seller: number;
    };
}

export const PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_SUCCESS = 'PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_SUCCESS';

export interface ProductDetailSetShowSellerPhoneApiSuccessAction
    extends ProductDetailSetShowSellerPhoneApiAction,
        APISuccessAction<ProductDetailSetShowSellerPhoneApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_SUCCESS;
    readonly sellerPhone: string;
}

export const PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_FAILURE = 'PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_FAILURE';

export interface ProductDetailSetShowSellerPhoneApiFailureAction
    extends ProductDetailSetShowSellerPhoneApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_FAILURE;
}

export const productDetailSetShowSellerPhoneApi = {
    request: (
        value: boolean,
        slug: string,
        phoneVerified: boolean,
        sellerId: number,
    ): ProductDetailSetShowSellerPhoneApiRequestAction => ({
        type: PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_REQUEST,
        value,
        slug,
        phoneVerified,
        sellerId,
    }),
    success: (
        value: boolean,
        slug: string,
        phoneVerified: boolean,
        sellerId: number,
        sellerPhone: string,
        response: ProductDetailSetShowSellerPhoneApiNormalizedResponse,
    ): ProductDetailSetShowSellerPhoneApiSuccessAction => ({
        type: PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_SUCCESS,
        value,
        slug,
        phoneVerified,
        sellerId,
        sellerPhone,
        response,
    }),
    failure: (
        value: boolean,
        slug: string,
        phoneVerified: boolean,
        sellerId: number,
        error: any,
    ): ProductDetailSetShowSellerPhoneApiFailureAction => ({
        type: PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE_API_FAILURE,
        value,
        slug,
        phoneVerified,
        sellerId,
        error,
    }),
};

export const PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE = 'PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE';

export interface ProductDetailSetShowSellerPhoneAction extends ProductDetailSetShowSellerPhoneApiAction {
    readonly type: typeof PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE;
}

export const productDetailSetShowSellerPhone = (
    value: boolean,
    slug: string,
    phoneVerified: boolean,
    sellerId: number,
): ProductDetailSetShowSellerPhoneAction => ({
    type: PRODUCT_DETAIL_SET_SHOW_SELLER_PHONE,
    value,
    slug,
    phoneVerified,
    sellerId,
});

export interface ProductDetailUndeleteProductApiAction extends Action {
    readonly productId: number;
}

export const PRODUCT_DETAIL_UNDELETE_PRODUCT_API_REQUEST = 'PRODUCT_DETAIL_UNDELETE_PRODUCT_API_REQUEST';

export interface ProductDetailUndeleteProductApiRequestAction
    extends ProductDetailUndeleteProductApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_UNDELETE_PRODUCT_API_REQUEST;
}

export interface ProductDetailUndeleteProductApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        product: number;
    };
}

export const PRODUCT_DETAIL_UNDELETE_PRODUCT_API_SUCCESS = 'PRODUCT_DETAIL_UNDELETE_PRODUCT_API_SUCCESS';

export interface ProductDetailUndeleteProductApiSuccessAction
    extends ProductDetailUndeleteProductApiAction,
        APISuccessAction<ProductDetailUndeleteProductApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_UNDELETE_PRODUCT_API_SUCCESS;
}

export const PRODUCT_DETAIL_UNDELETE_PRODUCT_API_FAILURE = 'PRODUCT_DETAIL_UNDELETE_PRODUCT_API_FAILURE';

export interface ProductDetailUndeleteProductApiFailureAction
    extends ProductDetailUndeleteProductApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_UNDELETE_PRODUCT_API_FAILURE;
}

export const productDetailUndeleteProductApi = {
    request: (productId: number): ProductDetailUndeleteProductApiRequestAction => ({
        type: PRODUCT_DETAIL_UNDELETE_PRODUCT_API_REQUEST,
        productId,
    }),
    success: (
        productId: number,
        response: ProductDetailUndeleteProductApiNormalizedResponse,
    ): ProductDetailUndeleteProductApiSuccessAction => ({
        type: PRODUCT_DETAIL_UNDELETE_PRODUCT_API_SUCCESS,
        productId,
        response,
    }),
    failure: (productId: number, error: any): ProductDetailUndeleteProductApiFailureAction => ({
        type: PRODUCT_DETAIL_UNDELETE_PRODUCT_API_FAILURE,
        productId,
        error,
    }),
};

export const PRODUCT_DETAIL_UNDELETE_PRODUCT = 'PRODUCT_DETAIL_UNDELETE_PRODUCT';

export interface ProductDetailUndeleteProductAction extends ProductDetailUndeleteProductApiAction {
    readonly type: typeof PRODUCT_DETAIL_UNDELETE_PRODUCT;
}

export const productDetailUndeleteProduct = (productId: number): ProductDetailUndeleteProductAction => ({
    type: PRODUCT_DETAIL_UNDELETE_PRODUCT,
    productId,
});

export interface ProductDetailLoadRelatedProductsApiAction extends Action {
    readonly slug: string;
    readonly start: number;
}

export const PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_REQUEST = 'PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_REQUEST';

export interface ProductDetailLoadRelatedProductsApiRequestAction
    extends ProductDetailLoadRelatedProductsApiAction,
        APIRequestAction {
    readonly type: typeof PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_REQUEST;
}

export interface ProductDetailLoadRelatedProductsApiNormalizedResponse extends NormalizedResponse {
    readonly result: {
        relatedProducts: number[];
    };
}

export const PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_SUCCESS = 'PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_SUCCESS';

export interface ProductDetailLoadRelatedProductsApiSuccessAction
    extends ProductDetailLoadRelatedProductsApiAction,
        APISuccessAction<ProductDetailLoadRelatedProductsApiNormalizedResponse> {
    readonly type: typeof PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_SUCCESS;
    readonly hasNext: boolean;
}

export const PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_FAILURE = 'PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_FAILURE';

export interface ProductDetailLoadRelatedProductsApiFailureAction
    extends ProductDetailLoadRelatedProductsApiAction,
        APIFailureAction {
    readonly type: typeof PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_FAILURE;
}

export const productDetailLoadRelatedProductsApi = {
    request: (slug: string, start: number): ProductDetailLoadRelatedProductsApiRequestAction => ({
        type: PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_REQUEST,
        slug,
        start,
    }),
    success: (
        slug: string,
        start: number,
        hasNext: boolean,
        response: ProductDetailLoadRelatedProductsApiNormalizedResponse,
    ): ProductDetailLoadRelatedProductsApiSuccessAction => ({
        type: PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_SUCCESS,
        slug,
        start,
        hasNext,
        response,
    }),
    failure: (slug: string, start: number, error: any): ProductDetailLoadRelatedProductsApiFailureAction => ({
        type: PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS_API_FAILURE,
        slug,
        start,
        error,
    }),
};

export const PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS = 'PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS';

export interface ProductDetailLoadRelatedProductsAction extends ProductDetailLoadRelatedProductsApiAction {
    readonly type: typeof PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS;
}

export const productDetailLoadRelatedProducts = (
    slug: string,
    start: number,
): ProductDetailLoadRelatedProductsAction => ({ type: PRODUCT_DETAIL_LOAD_RELATED_PRODUCTS, slug, start });
